import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, Switch, CircularProgress, Alert, Divider, Button } from '@mui/material';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { SettingsService } from '../../../api/services/settingsService';
import { extractTotalCount } from '../../../components/common/tableStyles';

export default function DeliveryChannelsTab({ setNotification, isActive }) {
    const [notificationChannels, setNotificationChannels] = useState([]);
    const [channelsLoading, setChannelsLoading] = useState(false);
    const [channelsError, setChannelsError] = useState(null);

    const [channelsPage, setChannelsPage] = useState(0);
    const [channelsLimit, setChannelsLimit] = useState(20);
    const [totalChannels, setTotalChannels] = useState(0);

    useEffect(() => {
        if (isActive) {
            fetchNotificationChannels();
        }
    }, [isActive, channelsPage, channelsLimit]);

    const fetchNotificationChannels = async () => {
        setChannelsLoading(true);
        setChannelsError(null);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const params = {
                page: channelsPage,
                limit: channelsLimit
            };
            const data = await SettingsService.getNotificationChannels(tenantId, params);
            const channelsArray = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
            setNotificationChannels(channelsArray);
            setTotalChannels(extractTotalCount(data, channelsArray));
        } catch (err) {
            console.error('Failed to fetch notification channels:', err);
            setChannelsError('Failed to load delivery channels.');
        } finally {
            setChannelsLoading(false);
        }
    };

    const handleToggleChannelStatus = async (channelType, currentStatus) => {
        const newStatus = !currentStatus;
        const previousChannels = [...notificationChannels];
        
        setNotificationChannels(notificationChannels.map(ch => 
            ch.channelType === channelType ? { ...ch, isEnabled: newStatus } : ch
        ));

        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            await SettingsService.updateNotificationChannel(channelType, { isEnabled: newStatus }, tenantId);
            setNotification({ open: true, message: `${channelType} ${newStatus ? 'enabled' : 'disabled'} successfully`, severity: 'success' });
        } catch (err) {
            console.error('Failed to toggle channel status:', err);
            setNotificationChannels(previousChannels);
            setNotification({ open: true, message: 'Failed to update channel status', severity: 'error' });
        }
    };

    if (channelsLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                <CircularProgress />
                <Typography color="text.secondary">Fetching channels...</Typography>
            </Box>
        );
    }

    if (channelsError) {
        return <Alert severity="error">{channelsError}</Alert>;
    }

    return (
        <Box>
            <Box sx={{ mb: 4, p: 3, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 2, display: 'flex', gap: 2 }}>
                <SettingsSuggestIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                    <Typography variant="h6" fontWeight="bold">Global Gateway Settings</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Enable or disable global delivery channels. If a channel is disabled here, it will override individual alert rule settings.
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {notificationChannels.map((channel) => (
                    <Grid item xs={12} md={4} key={channel.id}>
                        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #eee', position: 'relative', overflow: 'hidden' }}>
                            <Box sx={{ 
                                position: 'absolute', top: 0, left: 0, w: 4, height: '100%', 
                                bgcolor: channel.isEnabled ? 'success.main' : 'error.main' 
                            }} />
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ p: 1, bgcolor: 'primary.50', color: 'primary.main', borderRadius: 2, display: 'flex' }}>
                                        {channel.channelType === 'Email' ? <EmailIcon fontSize="medium" /> : channel.channelType === 'SMS' ? <SmsIcon fontSize="medium" /> : <NotificationsActiveIcon fontSize="medium" />}
                                    </Box>
                                    <Typography variant="h6" fontWeight="bold">{channel.channelType}</Typography>
                                </Box>
                                <Switch 
                                    checked={channel.isEnabled} 
                                    onChange={() => handleToggleChannelStatus(channel.channelType, channel.isEnabled)}
                                />
                            </Box>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Provider</Typography>
                                    <Typography variant="body2" fontWeight="bold">{channel.provider || 'Internal'}</Typography>
                                </Box>
                                {channel.config && Object.keys(channel.config).length > 0 ? (
                                    <Box sx={{ bgcolor: 'rgba(0,0,0,0.02)', p: 1.5, borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>CREDENTIALS & CONFIG</Typography>
                                        <Grid container spacing={1}>
                                            {channel.config?.from && (
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary" display="block">Sender ID / From</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{channel.config.from}</Typography>
                                                </Grid>
                                            )}
                                            {channel.config?.host && (
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary" display="block">SMTP Host</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{channel.config.host}</Typography>
                                                </Grid>
                                            )}
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="text.secondary" display="block">Authentication</Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>••••••••••••••••</Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>No external configuration required</Typography>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
            
            {notificationChannels.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button 
                        disabled={channelsPage === 0} 
                        onClick={() => setChannelsPage(prev => prev - 1)}
                        sx={{ mr: 2 }}
                    >
                        Previous Page
                    </Button>
                    <Button 
                        disabled={notificationChannels.length < channelsLimit} 
                        onClick={() => setChannelsPage(prev => prev + 1)}
                    >
                        Next Page
                    </Button>
                </Box>
            )}
        </Box>
    );
}
